import BaseModal from 'components/Modals/BaseModal';
import { useRef, useState } from 'react';
import { Button } from 'react-bootstrap';
import { BsEraser } from 'react-icons/bs';
import { IoClose, IoEllipsisHorizontal, IoReload } from 'react-icons/io5';
import { PiPlusBold } from 'react-icons/pi';
import { VMS_FEED_RUNTIME_CUTOFF_MS } from '../config/vms.config';
import styles from '../assets/ExacqVision.module.css';
import VideoPlayer from '../features/VideoPlayer/index.web';

export default function CameraGrid({
  compact = false,
  viewMode,
  selectedCameraIds,
  onSelectCamera,
  onClearCamera,
  onClearAllCameras,
  widgetRuntime,
}) {
  const [activeAudioIndex, setActiveAudioIndex] = useState(0);
  const [actionPanelIndex, setActionPanelIndex] = useState(null);
  const panelControlsRef = useRef([]);
  const lastActionPanelRef = useRef(null);
  const shouldSelectCameraRef = useRef(false);

  const openPanelActions = (panelIndex) => {
    lastActionPanelRef.current = panelIndex;
    setActionPanelIndex(panelIndex);
  };

  const finishPanelActions = () => {
    const panelIndex = lastActionPanelRef.current;
    const panelControl = panelControlsRef.current[panelIndex];
    if (shouldSelectCameraRef.current) {
      shouldSelectCameraRef.current = false;
      onSelectCamera(panelIndex, panelControl);
    } else {
      panelControl?.focus();
    }
  };

  return (
    <>
      <div
        className={styles['camera-grid']}
        data-count={viewMode.panelCount}
        data-compact={compact}
      >
        {Array.from({ length: viewMode.panelCount }, (_, index) => {
          const cameraId = selectedCameraIds[index];
          const hasCamera = cameraId !== undefined && cameraId !== null;

          return (
            <div
              className={`${styles['camera-panel']} position-relative`}
              key={index}
              data-filled={hasCamera}
              onContextMenu={
                hasCamera
                  ? (event) => {
                      event.preventDefault();
                      openPanelActions(index);
                    }
                  : undefined
              }
            >
              <Button
                ref={(element) => {
                  panelControlsRef.current[index] = element;
                }}
                type="button"
                variant={hasCamera ? 'light' : undefined}
                className={
                  hasCamera ? 'position-absolute top-0 end-0 m-2 p-2 z-3' : 'btn-transparent'
                }
                aria-label={
                  hasCamera
                    ? `Camera panel ${index + 1} actions`
                    : `Select camera for panel ${index + 1}`
                }
                aria-haspopup="dialog"
                onClick={(event) =>
                  hasCamera ? openPanelActions(index) : onSelectCamera(index, event.currentTarget)
                }
              >
                {hasCamera ? (
                  <IoEllipsisHorizontal size={20} aria-hidden="true" focusable="false" />
                ) : (
                  <PiPlusBold size={30} aria-hidden="true" focusable="false" />
                )}
              </Button>
              {hasCamera && (
                <div className="h-100 w-100 min-h-0 min-w-0 d-flex justify-content-center align-items-center position-relative">
                  <VideoPlayer
                    activeAudioIndex={activeAudioIndex}
                    ariaLabel={`Camera panel ${index + 1} feed`}
                    runtimeCutoffMs={VMS_FEED_RUNTIME_CUTOFF_MS}
                    setActiveAudioIndex={setActiveAudioIndex}
                    selfAudioIndex={index}
                    verticalShrink={viewMode.name === 'double'}
                    widgetRuntime={widgetRuntime}
                    src={`${process.env.PUBLIC_API_URL}/v2/integrations/device/camera/${encodeURIComponent(cameraId)}/video-stream/index.m3u8`}
                  />
                </div>
              )}
            </div>
          );
        })}
      </div>
      <BaseModal
        size="sm"
        show={actionPanelIndex !== null}
        handleShow={() => setActionPanelIndex(null)}
        onExited={finishPanelActions}
        restoreFocus={false}
        title="Camera actions"
        aria-label="Camera actions"
        container={widgetRuntime?.isPoppedOut ? widgetRuntime.hostElement : undefined}
      >
        <div className="d-flex flex-column gap-2">
          <Button
            type="button"
            onClick={() => {
              shouldSelectCameraRef.current = true;
              setActionPanelIndex(null);
            }}
          >
            <IoReload aria-hidden="true" /> Select Camera
          </Button>
          <Button
            type="button"
            onClick={() => {
              onClearCamera(actionPanelIndex);
              setActionPanelIndex(null);
            }}
          >
            <BsEraser aria-hidden="true" /> Clear Panel
          </Button>
          <Button
            type="button"
            onClick={() => {
              onClearAllCameras();
              setActionPanelIndex(null);
            }}
          >
            <IoClose aria-hidden="true" /> Clear All Panels
          </Button>
        </div>
      </BaseModal>
    </>
  );
}
